import {forwardRef, useMemo} from "react";
import {DefaultText, DefaultTextProps} from "../DefaultText/DefaultText";
import {Text} from "@mantine/core";

export type CurrencyFormatOptions = Omit<Intl.NumberFormatOptions, 'style'>

export type FormattedCurrencyProps = Omit<DefaultTextProps, 'value'> & {
  value?: number
  options?: CurrencyFormatOptions
  colorize?: boolean
}

export const getFormattedCurrency = (value: number | undefined, options: CurrencyFormatOptions | undefined = undefined): string | undefined => {
  if (typeof value === 'undefined') {
    return undefined
  }

  const formatOptions: Intl.NumberFormatOptions  = options ? {
    ...options,
    style: 'currency',
    currency: options.currency ?? "USD"
  } : {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }

  return new Intl.NumberFormat(undefined, formatOptions).format(value)
};

export const FormattedCurrency = forwardRef<HTMLDivElement, FormattedCurrencyProps>((props, ref) => {
  const {value,
    options,
    defaultValue,
    colorize = false,
    ...rest} = props;

  const formattedValue = useMemo(() => {
    if (typeof value === 'undefined') {
      return undefined
    }
    return getFormattedCurrency(value, options)
  }, [value, options])

  const color = useMemo(() => {
    if (colorize && typeof value !== 'undefined') {
      if (value > 0) {
        return "green"
      } else if (value < 0) {
        return "red"
      }
    }

    return undefined
  }, [value, colorize])

  if (!formattedValue) {
    return <DefaultText ref={ref} {...rest} defaultValue={defaultValue}/>
  }

  return (
    <Text {...rest} ref={ref} color={color}>
      {formattedValue}
    </Text>
  );
});
